import React, { useState, forwardRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

export const TOC_INTERFACE = {
  name: "",
  value: ""
};

const DynamicScrollIslandTOC = forwardRef(({ 
  data, 
  value, 
  setValue, 
  transition = { type: "spring", duration: 0.4, bounce: 0.1 },
  className = "",
  onOpenChange
}, ref) => {
  const [open, setOpen] = useState(false);

  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        onOpenChange?.(false);
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onOpenChange]);

  // Disable/enable body scroll when dropdown opens/closes
  React.useEffect(() => {
    if (open) {
      // Disable scrolling
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      // Enable scrolling
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
    }

    // Cleanup function to ensure scrolling is re-enabled when component unmounts
    return () => {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
    };
  }, [open]);

  const handleToggle = () => {
    const newState = !open;
    setOpen(newState);
    onOpenChange?.(newState);
  };

  const handleItemClick = (item) => {
    setValue(item);
    setOpen(false);
    onOpenChange?.(false);
  };

  return (
    <>
      <motion.div
        layout
        transition={transition}
        className={`dynamic-toc-container ${className} ${open ? 'open' : ''}`}
        style={{ 
          position: 'relative', // Changed from 'fixed' to 'relative'
          zIndex: 1000,
          borderRadius: '32px',
          background: 'white',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          border: '1px solid #e0e0e0',
          overflow: 'hidden',
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
          width: open ? '320px' : '250px',
          fontFamily: "Playfair Display, serif",
          marginBottom: '20px' // Add margin for spacing
        }}
      >
        <motion.button
          layout
          transition={transition}
          onClick={handleToggle}
          className="toc-trigger"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            padding: open ? '16px 20px 12px' : '12px 20px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: '1.5rem',
            fontWeight: '900',
            color: '#391B14',
            width: '100%',
            minHeight: '44px',
            fontFamily: "Playfair Display, serif"
          }}
        >
          <motion.span
            layout
            transition={transition}
          >
            {value?.name ?? "Select..."}
          </motion.span>
          
          <motion.div
            animate={{ rotate: open ? 180 : 0 }}
            transition={transition}
            style={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg
              width="26"
              height="26"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7 10L12 15L17 10"
                stroke="#391B14"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </motion.div>
        </motion.button>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={transition}
              style={{ borderTop: '1px solid #e0e0e0' }}
            >
              <motion.div 
                layout
                style={{ padding: '8px 0' }}
              >
                {data.map((item, index) => (
                  <motion.button
                    key={item.value || item.name}
                    onClick={() => handleItemClick(item)}
                    className={`toc-item ${value?.value === item.value ? 'active' : ''}`}
                    whileHover={{ backgroundColor: value?.value === item.value ? '#896446' : '#f5f5f5' }}
                    whileTap={{ scale: 0.98 }}
                    transition={{ duration: 0.2 }}
                    style={{
                      width: '100%',
                      padding: '12px 20px',
                      background: value?.value === item.value ? '#896446' : 'transparent',
                      color: value?.value === item.value ? 'white' : '#666',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      textAlign: 'left',
                      transition: 'all 0.2s ease', 
                      fontFamily: "Playfair Display, serif"
                    }}
                  >
                    {item.name}
                  </motion.button>
                ))}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => {
              setOpen(false);
              onOpenChange?.(false);
            }}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0, 0, 0, 0.2)',
              zIndex: 999,
              backdropFilter: 'blur(2px)',
              WebkitBackdropFilter: 'blur(2px)'
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
});

DynamicScrollIslandTOC.displayName = "DynamicScrollIslandTOC";

export default DynamicScrollIslandTOC;
